import { GridApiPro } from '@mui/x-data-grid-pro';
import { ScrollArrows } from '@schooly/components/data-grid';
import React, { FC, useCallback } from 'react';

interface HeaderCellPosition {
  offsetLeft: number;
  width: number;
}

export interface AssessmentMarkbookScrollArrowsProps {
  apiRef: React.MutableRefObject<GridApiPro | null> | null;
  containerRef: React.RefObject<HTMLDivElement>;
  pinnedColumns: number;
  colWidths: number[];
}

export const AssessmentMarkbookScrollArrows: FC<AssessmentMarkbookScrollArrowsProps> = ({
  apiRef,
  containerRef,
  pinnedColumns,
  colWidths,
}) => {
  const pinnedColumnsWidth = colWidths.slice(0, pinnedColumns).reduce((a, b) => a + b, 0);
  const scrollableColumnWidths = colWidths.slice(pinnedColumns);

  const headerCells = scrollableColumnWidths.reduce((acc: HeaderCellPosition[], width) => {
    const offsetLeft =
      acc.length > 0 ? acc[acc.length - 1].offsetLeft + acc[acc.length - 1].width : 0;
    return [...acc, { offsetLeft, width }];
  }, []);

  const showLeftArrow = useCallback(() => {
    return (apiRef?.current?.getScrollPosition().left ?? 0) > 0;
  }, [apiRef]);

  const showRightArrow = useCallback(() => {
    const leftPosition = apiRef?.current?.getScrollPosition().left ?? 0;

    const scrollWidth = headerCells.reduce((sum, cell) => sum + cell.width, 0);
    const containerWidth = containerRef.current?.offsetWidth ?? 0;
    const widthOfVisibleArea = containerWidth - pinnedColumnsWidth;
    const leftMaxPosition = scrollWidth - widthOfVisibleArea;

    return leftMaxPosition > leftPosition * 1.01;
  }, [apiRef, containerRef, headerCells, pinnedColumnsWidth]);

  const handleArrowClick = (direction: 'left' | 'right') => {
    return () => {
      if (!apiRef?.current) return;

      const leftPosition = apiRef.current.getScrollPosition().left ?? 0;

      let scrollTo;
      switch (direction) {
        case 'right': {
          scrollTo = headerCells.find((cell) => {
            return cell.offsetLeft > Math.floor(leftPosition);
          });

          break;
        }
        case 'left': {
          scrollTo = [...headerCells].reverse().find((cell) => {
            return cell.offsetLeft < Math.floor(leftPosition);
          });
          break;
        }
        default: {
          const _: never = direction;
          return _;
        }
      }
      if (!scrollTo) return;

      return apiRef?.current.scroll({
        left: scrollTo.offsetLeft,
      });
    };
  };

  return (
    <ScrollArrows
      onArrowClick={handleArrowClick}
      shouldShowRightArrow={showRightArrow}
      shouldShowLeftArrow={showLeftArrow}
      apiRef={apiRef}
    />
  );
};
